import React, { createContext, useContext, useState, useCallback } from 'react';

const GlobalErrorContext = createContext(null);

export function GlobalErrorProvider({ children }) {
  const [errors, setErrors] = useState([]);
  const [currentError, setCurrentError] = useState(null);

  const addError = useCallback((error, options = {}) => {
    const errorObj = {
      id: Date.now() + Math.random(),
      message: error?.message || error || 'An unexpected error occurred',
      type: options.type || 'error',
      timeout: options.timeout !== undefined ? options.timeout : 5000,
      details: options.details,
      timestamp: new Date().toISOString(),
    };

    setErrors(prev => [...prev, errorObj]);
    setCurrentError(errorObj);

    if (errorObj.timeout > 0) {
      setTimeout(() => {
        dismissError(errorObj.id);
      }, errorObj.timeout);
    }
  }, []);

  const dismissError = useCallback((id) => {
    setErrors(prev => prev.filter(e => e.id !== id));
    setCurrentError(prev => prev?.id === id ? null : prev);
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
    setCurrentError(null);
  }, []);

  return (
    <GlobalErrorContext.Provider value={{ addError, dismissError, clearAllErrors, errors, currentError }}>
      {children}
      <GlobalErrorBanner />
    </GlobalErrorContext.Provider>
  );
}

export function useGlobalError() {
  const context = useContext(GlobalErrorContext);
  if (!context) {
    throw new Error('useGlobalError must be used within GlobalErrorProvider');
  }
  return context;
}

function GlobalErrorBanner() {
  const { errors, dismissError } = useGlobalError();

  if (errors.length === 0) return null;

  return (
    <div className="global-error-container">
      {errors.map((error, index) => (
        <div 
          key={error.id} 
          className={`global-error-banner global-error-banner--${error.type}`}
          style={{ top: `${index * 70 + 20}px` }}
        >
          <div className="global-error-content">
            <span className="global-error-icon">
              {error.type === 'success' ? '✓' : error.type === 'warning' ? '⚠' : '✕'}
            </span>
            <span className="global-error-message">{error.message}</span>
          </div>
          {error.details && (
            <details className="global-error-details">
              <summary>Details</summary>
              <pre>{typeof error.details === 'object' ? JSON.stringify(error.details, null, 2) : error.details}</pre>
            </details>
          )}
          <button 
            className="global-error-dismiss"
            onClick={() => dismissError(error.id)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

export default GlobalErrorProvider;
